import type {Icon} from '@primer/octicons-react'
import {
  BlockType,
  type FilterProviderOptions,
  type FilterProviderType,
  type AnyBlock,
  type MutableFilterBlock,
  type SuppliedFilterProviderOptions,
  defaultFilterProviderOptions,
  type FilterKey,
  type FilterValueValidator,
  type IndexedBlockValueItem,
  type BaseFilterValue,
} from '../types'
import {capitalize, getUnescapedFilterValue, shouldProviderShowSuggestions} from '../utils'

export class RootFilterProvider implements FilterKey {
  key: string
  displayName: string
  description?: string
  priority: number
  type: FilterProviderType
  icon: Icon
  options: FilterProviderOptions

  constructor(filterKey: FilterKey, options?: SuppliedFilterProviderOptions) {
    Object.assign(this, filterKey)
    this.displayName = filterKey.displayName ?? capitalize(filterKey.key)

    this.options = {
      ...defaultFilterProviderOptions,
      priority: filterKey.priority,
      ...options,
      filterTypes: {...defaultFilterProviderOptions.filterTypes, ...options?.filterTypes},
    }
    this.priority = this.options.priority
  }

  shouldGetSuggestions(filterBlock: AnyBlock | MutableFilterBlock): boolean {
    return (
      filterBlock &&
      filterBlock.type !== BlockType.Space &&
      shouldProviderShowSuggestions(filterBlock, this.options.filterTypes.multiValue)
    )
  }
}

const isBaseFilterValue = (value: unknown): value is BaseFilterValue =>
  !!value && typeof value === 'object' && 'value' in value && typeof (value as BaseFilterValue).value === 'string'

export class ValueValidator<T = unknown> extends RootFilterProvider implements FilterValueValidator<T> {
  validateValue(
    value: IndexedBlockValueItem,
    data: T[] | null,
  ): false | Partial<IndexedBlockValueItem> | null | undefined {
    const extractedValue = getUnescapedFilterValue(value.value)
    if (!data) return false

    const valueObject = data.find(d => isBaseFilterValue(d) && d?.value === extractedValue)
    if (!valueObject) return false

    return {}
  }
}
